import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ChangeDetectionStrategy
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
    AbstractControl,
    ReactiveFormsModule,
    UntypedFormBuilder,
    Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule } from '@jsverse/transloco';
import { ClientService } from 'src/app/core/services/client.service';
import { Client } from 'src/app/domain/models';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import { BusyableButtonComponent } from '../../../../shared/components';
import { AccountInfo } from '../../models';
import { PrivacyPolicyComponent } from '../privacy-policy/privacy-policy.component';
import { TermsOfUseComponent } from '../terms-of-use/terms-of-use.component';

@Component({
    selector: 'app-account-setup',
    templateUrl: './account-setup.component.html',
    styleUrls: ['./account-setup.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        TranslocoModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatIcon,

        TermsOfUseComponent,
        PrivacyPolicyComponent,
        BusyableButtonComponent,
        FlexLayoutModule,
        MatButtonModule,
        MatInputModule,
        MatCheckboxModule
    ]
})
export class AccountSetupComponent implements OnInit {
    @Output() accountInfo = new EventEmitter<AccountInfo>();
    @Output() back = new EventEmitter<void>();

    /** Set by the parent while the code request (including the CAPTCHA) is in flight. */
    @Input() busy = false;

    /** Set by the parent when the CAPTCHA could not be completed. */
    @Input() submitFailed = false;

    formGroup = this._fb.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        emailOrPhone: [null, Validators.required],
        receivesNotifications: [true, Validators.required],
        agree: [false, Validators.requiredTrue]
    });

    showHelpText = false;
    displayTerms = false;
    displayPrivacy = false;
    client: Client | undefined;

    constructor(
        private readonly _fb: UntypedFormBuilder,
        private _googleAnalyticsService: GoogleAnalyticsService,
        private readonly _client: ClientService
    ) {}

    ngOnInit() {
        this._client.latest.subscribe((client) => {
            this.client = client;
        });
    }

    get firstName(): AbstractControl | null {
        return this.formGroup.get('firstName');
    }

    get lastName(): AbstractControl | null {
        return this.formGroup.get('lastName');
    }

    get emailOrPhone(): AbstractControl | null {
        return this.formGroup.get('emailOrPhone');
    }

    get receivesNotifications(): AbstractControl | null {
        return this.formGroup.get('receivesNotifications');
    }

    get agree(): AbstractControl | null {
        return this.formGroup.get('agree');
    }

    onSubmit(e: any): void {
        if (this.formGroup.valid) {
            this.accountInfo.next({
                firstName: this.firstName?.value,
                lastName: this.lastName?.value,
                emailOrPhoneNumber: this.emailOrPhone?.value,
                receivesNotifications: this.receivesNotifications?.value,
                acceptedPrivacyPolicy: this.agree?.value
            });
        }
    }

    goBack(): void {
        this.back.next();
    }

    displayInfo(window: string): void {
        this.displayTerms = this.displayPrivacy = false;
        if (window == 'terms') {
            this.displayTerms = true;
        } else {
            this.displayPrivacy = true;
        }
        this.showHelpText = true;
    }

    goBackPrevious(): void {
        this.showHelpText = !this.showHelpText;
    }
}
